/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Stack,
} from '@mui/material';
import {useEffect, useState, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox, {
  RTAddressSelectBox,
} from '../../component/SelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  putCompositionsRTUnit,
  getCompositionsRTUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {format, pullDownMenu} from '../../define';

/**
 * 親機として登録されている数(子機の数)を求める
 * @param parentList 親機として登録されているユニットIDの一覧
 * @param unitID ユニットID
 * @param editUnitParent 編集するユニットの親室外機のID
 * @returns 親機として登録されている数
 */
const asParentUseCount = (
  parentList: (string | undefined)[],
  unitID: string,
  editUnitParent: string | undefined
) => {
  //編集中ユニットの親機は、選択肢に含めるため0を返す
  if (unitID === editUnitParent) return 0;
  //親機として登録されている数(子機の数)を求める
  return parentList.reduce((result, parentID) => {
    if (parentID === unitID) result++;
    return result;
  }, 0);
};

/**
 * 熱源機編集ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const QtonHyozanEditDialogDialog = (props: {
  /** 修正する熱源機ID */
  unitID: string;
  /** 修正する熱源機の情報 */
  displayRTInfo: apiIF.resParameters.rtUnitInformations;
  /** 熱源機の情報（set関数） */
  setRTInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsRTUnit | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  //パスパラメータ取得
  const {siteID, gwID, systemType: rtType} = useParams();

  //編集前の室外機情報を取得
  const tempRTID = props.displayRTInfo['unit-id'];
  const tempUnitName =
    props.displayRTInfo['unit-name'] === undefined
      ? ''
      : props.displayRTInfo['unit-name'];
  const tempUnitSystem =
    props.displayRTInfo['system'] === undefined
      ? ''
      : props.displayRTInfo['system'];
  const tempUnitAddress =
    props.displayRTInfo['address'] === undefined
      ? ''
      : props.displayRTInfo['address'];
  const tempUnitType =
    props.displayRTInfo['unit-type'] === undefined
      ? 'QtonOpen'
      : props.displayRTInfo['unit-type'];
  const tempUnitModel =
    props.displayRTInfo['unit-model'] === undefined
      ? 'WCMC'
      : props.displayRTInfo['unit-model'];
  const tempUnitPowerSupply =
    props.displayRTInfo['power-supply'] === undefined
      ? 'threePhase'
      : props.displayRTInfo['power-supply'];
  const tempUnitVoltage =
    props.displayRTInfo['input-voltage'] === undefined
      ? ''
      : props.displayRTInfo['input-voltage'];
  const tempUnitParent =
    props.displayRTInfo['parent-unit'] === undefined ||
    props.displayRTInfo['parent-unit'] === ''
      ? '0'
      : props.displayRTInfo['parent-unit'];
  const tempIuInfo =
    props.displayRTInfo['indoor-unit-informations'] === undefined
      ? []
      : props.displayRTInfo['indoor-unit-informations'];
  const tempQtonNameplate =
    props.displayRTInfo['nameplate'] === undefined
      ? 'ESA30E-25'
      : props.displayRTInfo['nameplate'];
  const tempAdvancedSetting =
    props.displayRTInfo['advanced-setting'] === undefined
      ? 'inactive'
      : props.displayRTInfo['advanced-setting'];
  const tempQtonPumpModel =
    props.displayRTInfo['pump-model'] === undefined
      ? ''
      : props.displayRTInfo['pump-model'];

  const [unitName, setUnitName] = useState(tempUnitName); //熱源機の名前
  const [rtSystem] = useState(tempUnitSystem); //系統番号
  const [unitAddress, setUnitAddress] = useState(tempUnitAddress); //RTアドレス
  //prettier-ignore
  const [unitModel, setUnitModel] = useState<apiIF.reqParameters.putCompositionsRTUnit['unit-model']>(tempUnitModel); //熱源機モデル
  const [unitType, setUnitType] = useState(tempUnitType); //熱源機タイプ
  const [unitPowerSupply, setUnitPowerSupply] = useState(tempUnitPowerSupply); //電源の仕様
  const [unitVoltage, setUnitVoltage] = useState(tempUnitVoltage); //入力電圧
  const [rtParent, setRTParent] = useState(tempUnitParent); //親機設定
  const [iuInfo, setIuInfo] = useState(tempIuInfo); //冷媒
  const [parentSelectionItem, setParentSelectionItem] = useState<
    {value: string; label: string}[]
  >([]); //親子熱源機設定選択項目
  //prettier-ignore
  const [nameplate, setNameplate] = useState<apiIF.reqParameters.putCompositionsRTUnit['nameplate']>(tempQtonNameplate); //銘版
  //prettier-ignore
  const [pumpModel, setPumpModel] = useState<apiIF.reqParameters.putCompositionsRTUnit['pump-model']>(tempQtonPumpModel); //ポンプ型式
  //prettier-ignore
  const [advancedSetting, setAdvancedSetting] = useState<apiIF.reqParameters.putCompositionsRTUnit['advanced-setting']>(tempAdvancedSetting); //拡張設定
  //フラグ
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  const [rtParentDisable, setRTParentDisable] = useState(false); //親機設定項目非表示フラグ
  const isFirstRenderOrCancel = useRef(true); //初回レンダー or キャンセルフラグ

  //TextBoxのエラー状態
  const [unitNameError, setUnitNameError] = useState(false); //熱源機の名前
  const [unitVoltageError, setUnitVoltageError] = useState(false); //入力電圧
  const [serialNumberError, setSerialNumberError] = useState(false); //製造番号
  const [installDateError, setInstallDateError] = useState(false); //納入日
  const [electricPriceError, setElectricPriceError] = useState(false); //電気代単価
  const [co2EmissionFactorError, setCO2EmissionFactorError] = useState(false); //CO2排出係数
  const [unitPriceError1, setUnitPriceError1] = useState(false); //代替燃料単価1
  const [unitPriceError2, setUnitPriceError2] = useState(false); //代替燃料単価2

  const trans = useTranslation().t; //翻訳関数

  //入力項目のチェック
  useEffect(() => {
    if (
      unitName === '' ||
      unitAddress === '' ||
      unitVoltage === '' ||
      unitNameError ||
      unitVoltageError ||
      serialNumberError ||
      electricPriceError ||
      co2EmissionFactorError ||
      unitPriceError1 ||
      unitPriceError2 ||
      installDateError ||
      apiWait === true
    ) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [
    unitName,
    unitAddress,
    unitVoltage,
    unitNameError,
    unitVoltageError,
    serialNumberError,
    electricPriceError,
    co2EmissionFactorError,
    unitPriceError1,
    unitPriceError2,
    installDateError,
    apiWait,
  ]);

  //親熱源機のリストを作成
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || rtType === undefined) {
      return;
    }

    //熱源機の情報を取得
    getCompositionsRTUnit(siteID, gwID, {'max-result': 400}).then(response => {
      //熱源機タイプQtonでフィルター
      const qtonUnits = response['unit-informations']
        .filter(
          rtInfo =>
            rtInfo['unit-type'] === 'QtonOpen' ||
            rtInfo['unit-type'] === 'QtonClose'
        )
        //RTアドレスを昇順にソート
        .sort((a, b) => {
          return Number(a['address']) - Number(b['address']);
        });

      //親子室外機設定選択項目作成
      let tempParentSelectionItem = [
        {value: '0', label: trans('RTEditDialog.txtField.choices.label.none')},
      ];
      /** 親機として登録されているユニットIDの一覧 */
      const parentList = qtonUnits.map(rtInfo => rtInfo['parent-unit']);
      //prettier-ignore
      //編集中のユニットに子機が登録されている場合は、選択肢をNoneのみとする
      if (parentList.findIndex(parentUnitID => parentUnitID === props.unitID) === -1) {
        //親機として選択可能なユニットを抽出
        for (const qtonUnit of qtonUnits) {
          if (
            qtonUnit['system'] !== rtType.slice(2) ||
            qtonUnit['unit-name'] === undefined ||
            qtonUnit['parent-unit'] !== '' || //子機の場合
            asParentUseCount(parentList, qtonUnit['unit-id'], tempUnitParent) >=
              3 //子が3台以上登録されている場合
          ) {
            continue;
          }
          tempParentSelectionItem.push({
            value: qtonUnit['unit-id'],
            label: qtonUnit['unit-name'],
          });
        }
      }
      setParentSelectionItem(tempParentSelectionItem);
      setUnitModel(
        props.displayRTInfo['unit-model'] === undefined
          ? 'WCMC'
          : props.displayRTInfo['unit-model']
      );
      setPumpModel(props.displayRTInfo['pump-model']);
      setAdvancedSetting(props.displayRTInfo['advanced-setting']);
    });
  }, [props.displayRTInfo, trans]);

  //機種タイプ変更時処理
  useEffect(() => {
    switch (unitType) {
      case 'Hyozan':
        //熱源機モデルの値を室外機情報から取得した値とするため
        //初回レンダー時とキャンセル時はここで熱源機のモデルを設定しない
        if (isFirstRenderOrCancel.current === false) {
          setUnitModel('WLMC');
          setNameplate('HCCV1001E');
        }
        setRTParentDisable(true);
        setRTParent('');
        break;
      case 'QtonOpen':
      case 'QtonClose':
        //熱源機モデルの値を室外機情報から取得した値とするため
        //初回レンダー時とキャンセル時はここで熱源機のモデルを設定しない
        if (isFirstRenderOrCancel.current === false) {
          setUnitModel('WCMC');
          setNameplate('ESA30E-25');
        }
        setRTParentDisable(false);
        break;
      case 'Chiller':
        setUnitModel('B3UM');
        if (isFirstRenderOrCancel.current === false) {
          setNameplate('MCUS5001VHE-W');
        }
        setRTParentDisable(true);
        setRTParent('');
        break;
    }
    isFirstRenderOrCancel.current = false;
  }, [unitType]);

  //機種モデル変更時処理
  useEffect(() => {
    // 拡張設定が有効の場合は機種型式、ポンプ型式を変更しない
    if (advancedSetting === 'active') {
      return;
    }
    switch (nameplate) {
      case 'ESA30E-25':
        setUnitModel('WCMC');
        setPumpModel('PD-51JU2');
        break;
      case 'ESA30EH-25':
        setUnitModel('WCMD');
        setPumpModel('PD-51JU2');
        break;
      case 'ESA30EH2-25':
        setUnitModel('WCME');
        setPumpModel('PDC88-510C');
        break;
      case 'HCCV1001E': //fall through
      case 'HCCV1001EA':
        setUnitModel('WLMC');
        break;
      case 'HCCV2001E':
        setUnitModel('WLMF');
        break;
      case 'MCUS5001VHE-W': //fall through
      case 'MCUS5001VHE-W1': //fall through
      case 'MCUS5001VHE-W1A': //fall through
      case 'MCUS5001VHE-W2': //fall through
      case 'MCUS5001VHE-W2A':
        setUnitModel('B3UM');
    }
  }, [nameplate]);

  /**
   * OKボタン押下時処理
   * @returns
   */
  const editUnit = () => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      (rtSystem !== '1' && rtSystem !== '2')
    ) {
      return;
    }

    setAPIWait(true);

    //情報を修正(Put)
    const putData: apiIF.reqParameters.putCompositionsRTUnit = {
      'unit-name': unitName,
      'system': rtSystem,
      'address': unitAddress,
      'unit-type': unitType,
      'unit-model': unitModel,
      'nameplate': nameplate,
      'power-supply': unitPowerSupply,
      /* チラーの場合Textの表示部を400V固定とする。 */
      'input-voltage': unitType === 'Chiller' ? '400' : unitVoltage,
      'indoor-unit-informations': iuInfo,
    };
    //熱源機を編集
    switch (unitType) {
      case 'Hyozan':
        break;
      case 'QtonOpen':
      case 'QtonClose':
        putData['nameplate'] = nameplate === '' ? 'ESA30E-25' : nameplate;
        putData['pump-model'] = pumpModel === '' ? 'PD-51JU2' : pumpModel;
        putData['advanced-setting'] = advancedSetting;
        putData['parent-unit'] = rtParent === '0' ? '' : rtParent;
        break;
      case 'Chiller':
        //50固定だが将来的な仕様追加を想定してクエリ項目は残しておくためここで値を代入しておく
        putData['capacity'] = '50';
        break;
    }

    putCompositionsRTUnit(siteID, gwID, props.unitID, putData)
      .then(() => {
        //熱源機の情報を取得
        return getCompositionsRTUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['unit-informations'].sort(sortByUnicode('unit-name'));

        props.setRTInfo(response);
        props.setIsOpenDialog(false);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setUnitName(tempUnitName);
    setUnitAddress(tempUnitAddress);
    setUnitType(tempUnitType);
    setUnitModel(tempUnitModel);
    setNameplate(tempQtonNameplate);
    setUnitVoltage(tempUnitVoltage);
    setUnitPowerSupply(tempUnitPowerSupply);
    setRTParent(tempUnitParent);
    setIuInfo(tempIuInfo);
    setUnitNameError(false);
    setUnitVoltageError(false);
    setSerialNumberError(false);
    setInstallDateError(false);
    setElectricPriceError(false);
    setCO2EmissionFactorError(false);
    setUnitPriceError1(false);
    setUnitPriceError2(false);
    //現在の設定値と編集前の値が異なる場合、フラグをtrueにする
    if (unitType !== tempUnitType) {
      isFirstRenderOrCancel.current = true;
    }
  };

  /**
   * 銘版選択セレクトボックス
   * @param unitType 機種タイプ
   * @returns jsx.element
   */
  const UnitModelSelect = (props: {
    unitType: 'QtonOpen' | 'QtonClose' | 'Hyozan' | 'Chiller';
  }) => {
    switch (props.unitType) {
      case 'QtonOpen': // fallthrough
      case 'QtonClose':
        return (
          <TextFieldSelectBox
            label={trans('RTAddDialog.dialogBox.txtField.label.unitModel')}
            choices={pullDownMenu.RT_UNIT_LABEL_QTON}
            setValue={setNameplate}
            value={nameplate}
            id="mv-selectAddRtUnitModel"
            class="gwSetting__contents__selectBox--rtModelQton"
          />
        );
      case 'Hyozan':
        return (
          <TextFieldSelectBox
            label={trans('RTAddDialog.dialogBox.txtField.label.unitModel')}
            choices={pullDownMenu.RT_UNIT_LABEL_HYOZAN}
            setValue={setNameplate}
            value={nameplate}
            id="mv-selectAddRtUnitModel"
            class="gwSetting__contents__selectBox--rtModelHyozan"
          />
        );
      case 'Chiller':
        return (
          <TextFieldSelectBox
            label={trans('RTAddDialog.dialogBox.txtField.label.unitModel')}
            choices={pullDownMenu.RT_UNIT_LABEL_CHILLER}
            setValue={setNameplate}
            value={nameplate}
            id="mv-selectAddRtUnitModel"
            class="gwSetting__contents__selectBox--rtModelChiller"
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('RTEditDialog.title')}
        </DialogTitle>
        <DialogContent>
          {/* ダイアログ内に表示するメッセージ */}
          <DialogContentText>{trans('RTEditDialog.msg')}</DialogContentText>
          <Stack spacing={1.5}>
            {/* 熱源機名称 */}
            <ChkInputTextbox
              setValue={setUnitName}
              setError={setUnitNameError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={trans('RTEditDialog.txtField.label.unitName')}
              value={unitName}
              id="mv-inputEditRtName"
              class="gwSetting__contents__textBox--unitName"
              helperText=" "
              errHelperText={trans('RTEditDialog.txtField.errHelperTxt')}
              variant="standard"
              autoFocus={true}
              margin="dense"
              chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
            />
            {/* アドレス */}
            <RTAddressSelectBox
              setAddress={setUnitAddress}
              label={trans('RTEditDialog.txtField.label.remoteTerminalAddress')}
              default={tempUnitAddress}
              id="mv-selectEditRtUnitAddr"
              class="gwSetting__contents__textBox--rtAddress"
            />
            {/* 電圧 */}
            <ChkInputTextbox
              /* チラーは電源電圧400V固定の為、TextBox変更不可とする。 */
              disabled={unitType === 'Chiller'}
              setValue={setUnitVoltage}
              setError={setUnitVoltageError}
              chkInputTxtOK={format.VOLTAGE_PATTERN_OK}
              type="text"
              label={trans('RTEditDialog.txtField.label.voltage')}
              /* チラーの場合Textの表示部を400V固定とする対応。実際の値はaddUnitDataでセット。 */
              value={unitType === 'Chiller' ? '400' : unitVoltage}
              id="mv-inputEditRtVoltage"
              class="gwSetting__contents__textBox--voltage"
              helperText=" "
              errHelperText={trans('RTEditDialog.txtField.errHelperTxt')}
              variant="standard"
            />
            {/* 電源の種類 */}
            <TextFieldSelectBox
              label={trans('RTEditDialog.txtField.label.powerSupply')}
              choices={pullDownMenu.POWER_SUPPLY_TYPE}
              setValue={setUnitPowerSupply}
              default={unitPowerSupply}
              id="mv-selectEditRtPowSply"
              class="gwSetting__contents__selectBox--powerSupply"
            />
            {/* 熱源機タイプ */}
            <TextFieldSelectBox
              label={trans('RTEditDialog.txtField.label.unitType')}
              choices={pullDownMenu.RT_UNIT_TYPE}
              setValue={setUnitType}
              default={unitType}
              id="mv-selectEditRtUnitType"
              class="gwSetting__contents__selectBox--rtType"
            />
            {/* 熱源機モデル */}
            <UnitModelSelect unitType={unitType} />
            {/* 親熱源機の選択 */}
            {rtParentDisable === false && parentSelectionItem.length > 0 ? (
              <TextFieldSelectBox
                label={trans('RTEditDialog.txtField.label.parentOutdoorUnit')}
                choices={parentSelectionItem}
                setValue={setRTParent}
                default={rtParent}
                id="mv-editParentRtSelect"
                class="gwSetting__contents__textBox--unitName"
              />
            ) : rtParentDisable === false &&
              parentSelectionItem.length === 0 ? (
              <TextField
                label={trans('RTEditDialog.txtField.label.parentOutdoorUnit')}
                variant="standard"
                defaultValue={trans(
                  'RTEditDialog.txtField.defaultValue.loadingMsg'
                )}
                disabled
              />
            ) : (
              <></>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('RTEditDialog.buttonTxt.cancel')}
            id={`mv-btnCancelEditRt${tempUnitName}`}
          />
          <PrimaryButton
            onClick={editUnit}
            buttonText={trans('RTEditDialog.buttonTxt.ok')}
            id={`mv-btnOkEditRt${tempUnitName}`}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${tempUnitName}EditErrDlgBtn`}
      />
    </>
  );
};
export default QtonHyozanEditDialogDialog;
